import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { DateConversionUtils } from '../../utils/date-conversion-utils';

export type FormattedDateType = 'shorthand' | 'ngbDate' | 'utcString' | 'jsDate' | 'nativeDateInput';

export class FormattedDate {
  public shortHandDate: string = ''; // DD/MM/YYYY
  public longFormDate: string = ''; // January 1, 2024
  public abbreviated: string = ''; // Jan 1, 2024
  public ngbDate: NgbDate | null = null;
  // Full UTC ISO instant (YYYY-MM-DDT00:00:00.000Z). Retained for consumers that still need a real
  // instant (e.g. combining a date with a separately-picked time). Not the canonical value emitted by
  // date-only pickers - use `nativeDateInput` for that.
  public utcString: string = '';
  public jsDate: Date | null = null;
  // YYYY-MM-DD - the canonical wire-format value for DateOnly fields, and also the native <input type=date> shape.
  public nativeDateInput: string = '';
  public isValid: boolean = false;

  constructor(value: any, type: FormattedDateType) {
    if (!value) {
      return;
    }

    try {
      const baseDate = this.convertToJSDate(value, type);

      if (baseDate && !isNaN(baseDate.getTime())) {
        this.jsDate = baseDate;
        this.isValid = true;
        this.generateAllFormats(baseDate);
      } else if (type === 'shorthand') {
        this.shortHandDate = value;
      }
    } catch {
      if (type === 'shorthand') {
        this.shortHandDate = value;
      }
    }
  }

  private convertToJSDate(value: any, type: FormattedDateType): Date | null {
    switch (type) {
      case 'shorthand':
        return DateConversionUtils.shorthandToDate(value);
      case 'ngbDate':
        return DateConversionUtils.ngbDateToDate(value);
      case 'utcString':
        return new Date(value);
      case 'jsDate':
        return new Date(value);
      case 'nativeDateInput':
        return new Date(value + 'T00:00:00.000Z');
      default:
        return new Date(value);
    }
  }

  private generateAllFormats(date: Date): void {
    this.shortHandDate = DateConversionUtils.dateToShorthand(date);
    this.longFormDate = DateConversionUtils.dateToLongform(date);
    this.abbreviated = DateConversionUtils.dateToAbbreviated(date);
    this.nativeDateInput = DateConversionUtils.dateToNativeInput(date);
    this.ngbDate = DateConversionUtils.dateToNgbDate(date);
    this.utcString = date.toISOString();
  }

  static fromShorthand(shorthandDate: string): FormattedDate {
    return new FormattedDate(shorthandDate, 'shorthand');
  }

  static fromNgbDate(ngbDate: NgbDate): FormattedDate {
    return new FormattedDate(ngbDate, 'ngbDate');
  }

  static fromUtcString(utcString: string): FormattedDate {
    return new FormattedDate(utcString, 'utcString');
  }

  static fromNativeDateInput(nativeDateString: string): FormattedDate {
    return new FormattedDate(nativeDateString, 'nativeDateInput');
  }

  static fromJsDate(date: Date): FormattedDate {
    return new FormattedDate(date, 'jsDate');
  }
}
